import { ProductPageHeader } from '@design-system/product-page-header';
import {
  getLocalStorageData,
  removeLocalStorageData,
} from '@phx-husky/local-storage';
import { graphql } from 'gatsby';
import { kebabCase } from 'lodash';
import React, { FC, useEffect } from 'react';

import Adimo from '~/common/Adimo';
import { checkAdimoAvailable } from '~/common/Adimo/utils';
import BazaarVoice from '~/common/BazaarVoice';
import Mikmak from '~/common/MikMak';
import { checkMikMakAvailable } from '~/common/MikMak/utils';
import PriceSpider from '~/common/PriceSpider';
import { PriceSpiderModel } from '~/common/PriceSpider/models';
import { checkPriceSpiderAvailable } from '~/common/PriceSpider/utils';
import BodyRender from '~/components/BodyRender';
import { Layout } from '~/layout/Layout';
import { productPageHeaderMapper } from '~/mappers/productPageHeader';
import { gtmService } from '~/services/gtm';
import { GTM_LOCALSTORAGE_DATA } from '~/utils/constants';
import withContentstackPreview from '~/utils/livePreview';

import { ProductPageData } from './models';

const BASE_CLASS = 'l2-product-page';

const ProductPage: FC<ProductPageData> = ({
  data: {
    contentstackL2ProductPage,
    contentstackHeader,
    contentstackFooter,
    contentstackInfoBar,
    contentstackSettings,
  },
  pageContext: { uid, locale, seoTitle, brand, pageTemplate, fullPath },
}) => {
  if (typeof window === 'undefined')
    console.info('Building:', JSON.stringify({ uid, locale, seoTitle }));

  const {
    seo,
    body,
    fields: { productData },
    categories,
  } = contentstackL2ProductPage;
  const {
    bazaarvoiceUrl,
    siteUrl,
    bazaarvoiceProductFieldName,
    priceSpider,
    adimo,
    shoppablePlugin,
    pluginInformationText,
    mikmak,
  } = contentstackSettings;

  const isPriceSpiderAvailable = checkPriceSpiderAvailable(
    shoppablePlugin,
    priceSpider
  );
  const isAdimoAvailable = checkAdimoAvailable(shoppablePlugin, adimo);
  const heroImageData = productData.heroImage.url
    ? [
        {
          property: 'og:image',
          content: productData.heroImage.url,
        },
      ]
    : [];
  const isMikMakAvailable = checkMikMakAvailable(shoppablePlugin, mikmak);

  const lastProductIndex = getLocalStorageData(
    GTM_LOCALSTORAGE_DATA.lastProductIndex
  );
  gtmService.emitViewItem(productData, pageTemplate, lastProductIndex);

  useEffect(() => {
    const handleBeforeUnload = () => {
      removeLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      removeLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex);
    };
  }, []);

  const categoryClasses = categories.map(
    ({ title }) => `${BASE_CLASS}--${kebabCase(title)}`
  );

  return (
    <Layout
      {...{
        seo: {
          ...seo,
          additionalMeta: [...seo.additionalMeta, ...heroImageData],
          pageType: 'og:product',
        },
        header: contentstackHeader,
        footer: contentstackFooter,
        pageUid: uid,
        locale,
        brand,
        className: [BASE_CLASS, ...categoryClasses].join(' '),
        infoBar: {
          ...contentstackInfoBar,
        },
        fullPath,
      }}
    >
      {bazaarvoiceUrl ? (
        <BazaarVoice
          url={bazaarvoiceUrl}
          productData={productData}
          productId={productData[bazaarvoiceProductFieldName]}
          brand={brand}
          siteUrl={siteUrl}
          lang={locale}
        />
      ) : null}
      {isPriceSpiderAvailable ? (
        <PriceSpider
          priceSpider={priceSpider as PriceSpiderModel}
          lang={locale}
        />
      ) : null}
      {isAdimoAvailable ? <Adimo url={adimo!.url!} /> : null}
      {isMikMakAvailable ? <Mikmak appId={mikmak?.appId!} /> : null}
      {productData ? (
        <ProductPageHeader
          {...productPageHeaderMapper({
            ...contentstackL2ProductPage,
            productId: productData[bazaarvoiceProductFieldName],
            isPriceSpiderAvailable,
            isAdimoAvailable,
            adimoKey: adimo?.key,
            adimoButton: adimo?.buttonText,
            isMikMakAvailable,
            mikmakAppId: mikmak?.appId,
            mikmakButton: mikmak?.buttonText,
            pluginInformationText,
            inventoryQuantity: 0,
          })}
        />
      ) : null}
      <BodyRender
        items={body}
        externalData={{ salsifyExternalData: productData }}
        currentPageUid={uid}
      />
    </Layout>
  );
};

export const query = graphql`
  query ProductPage($id: String, $locale: String, $brand: [String]) {
    contentstackL2ProductPage(
      id: { eq: $id }
      publish_details: { locale: { eq: $locale } }
    ) {
      seo {
        ...SeoFragment
      }
      body {
        ...BodyFragment
      }
      ...L2ProductPageFragment
      variantSelector: variant_selector {
        variants {
          ...L2ProductPageFragment
        }
      }
      categories {
        title
      }
    }
    contentstackHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderFragment
    }
    contentstackSettings(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...SettingsFragment
    }
    contentstackInfoBar(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...InfoBarFragment
    }
    contentstackFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterFragment
    }
  }
`;

export default withContentstackPreview(ProductPage, 'productpage');
